import { Injectable, NgZone, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../csr-web-api.service';
import { NotificationResponse } from '../models';
import { SignalRNotificationService } from './signalr-notification.service';
import {
  NotificationIconType,
  getNotificationIconType,
  notificationDeepLinkRoute
} from '../features/learner/notifications/notification-routing.util';

export type ToastIcon = NotificationIconType;

export interface ToastItem extends NotificationResponse {
  icon: ToastIcon;
  route: string[] | null;
  durationMs: number;
  remainingMs: number;
  progressPercent: number;
  isPaused: boolean;
  timerRef?: ReturnType<typeof setInterval>;
}

@Injectable({
  providedIn: 'root'
})
export class ToastNotificationService {
  private readonly router = inject(Router);
  private readonly api = inject(CsrWebApiService);
  private readonly signalR = inject(SignalRNotificationService);
  private readonly zone = inject(NgZone);

  readonly toasts = signal<ToastItem[]>([]);
  private readonly maxVisibleToasts = 3;
  private readonly defaultDurationMs = 6500;

  constructor() {
    this.signalR.notification$.subscribe((notification) => {
      this.zone.run(() => {
        this.showFromSignalR(notification);
      });
    });
  }

  showFromSignalR(notification: NotificationResponse): void {
    const icon = getNotificationIconType(notification);
    const route = notificationDeepLinkRoute(notification);
    const id = notification.id || (notification as any).Id || `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    this.addToast({
      ...notification,
      id,
      icon,
      route
    });
  }

  dismiss(id: string): void {
    if (!id) return;
    const current = this.toasts();
    const target = current.find((t) => t.id === id);
    if (target?.timerRef) {
      clearInterval(target.timerRef);
    }
    this.toasts.set(current.filter((t) => t.id !== id));
  }

  pause(id: string): void {
    this.toasts.update((list) =>
      list.map((t) => (t.id === id ? { ...t, isPaused: true } : t))
    );
  }

  resume(id: string): void {
    this.toasts.update((list) =>
      list.map((t) => (t.id === id ? { ...t, isPaused: false } : t))
    );
  }

  async onToastClick(toast: ToastItem): Promise<void> {
    this.dismiss(toast.id);

    const isBackendNotification = !!toast.id && !toast.id.startsWith('toast-');

    // If it's a real backend notification that hasn't been read, mark it as read
    if (isBackendNotification && !toast.isRead) {
      try {
        await firstValueFrom(this.api.markLearnerNotificationAsRead(toast.id));
        toast.isRead = true;
      } catch {
        // Continue navigation even if network mark-as-read fails
      }
    }

    if (toast.route && toast.route.length > 0) {
      await this.router.navigate(toast.route);
    } else if (isBackendNotification) {
      await this.router.navigate(['/notifications']);
    }
  }

  private addToast(
    item: Partial<ToastItem> & Pick<ToastItem, 'id' | 'title' | 'body' | 'icon'>
  ): void {
    if (item.id) {
      this.dismiss(item.id);
    }

    // Dismiss oldest if reached capacity limit
    const current = this.toasts();
    if (current.length >= this.maxVisibleToasts) {
      const oldest = current[0];
      this.dismiss(oldest.id);
    }

    const durationMs = item.durationMs ?? this.defaultDurationMs;
    const toast: ToastItem = {
      type: 'info',
      isRead: false,
      isSeen: false,
      createdAt: new Date().toISOString(),
      route: null,
      ...item,
      title: item.title || 'New Notification',
      body: item.body || '',
      durationMs,
      remainingMs: durationMs,
      progressPercent: 100,
      isPaused: false,
      timerRef: this.createToastTimer(item.id, durationMs)
    };

    this.toasts.update((list) => [...list, toast]);
  }

  private createToastTimer(toastId: string, durationMs: number): ReturnType<typeof setInterval> {
    const stepMs = 50;
    const timerRef = setInterval(() => {
      this.zone.run(() => {
        const item = this.toasts().find((t) => t.id === toastId);
        if (!item) {
          clearInterval(timerRef);
          return;
        }

        if (item.isPaused) {
          return;
        }

        const nextRemaining = item.remainingMs - stepMs;
        if (nextRemaining <= 0) {
          clearInterval(timerRef);
          this.dismiss(toastId);
          return;
        }

        const progressPercent = Math.max(0, (nextRemaining / durationMs) * 100);
        this.toasts.update((list) =>
          list.map((t) =>
            t.id === toastId
              ? { ...t, remainingMs: nextRemaining, progressPercent }
              : t
          )
        );
      });
    }, stepMs);

    return timerRef;
  }
}
